import { useState, useEffect } from "react";
import clsx from "clsx";
import { formatEuro } from "../../lib/format";

interface Props {
  label: string;
  value?: number;
  onChange: (v: number | undefined) => void;
  min?: number;
  max?: number;
  step?: number;
  hint?: string;
  optional?: boolean;
}

export default function BudgetSlider({
  label,
  value,
  onChange,
  min = 5000,
  max = 100000,
  step = 1000,
  hint,
  optional,
}: Props) {
  const id = `budget-slider-${label.toLowerCase().replace(/\s+/g, "-")}`;
  const [inputVal, setInputVal] = useState(value !== undefined ? String(value) : "");

  // Sync when parent resets
  useEffect(() => {
    setInputVal(value !== undefined ? String(value) : "");
  }, [value]);

  const hasValue = value !== undefined;
  const sliderValue = value ?? min;
  const thumbPct = ((sliderValue - min) / (max - min)) * 100;

  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-1">
        <label htmlFor={id} className={clsx("text-sm font-medium", optional ? "text-neko-gray" : "text-neko-gray-light")}>
          {label}
          {optional && <span className="text-neko-gray font-normal"> (optional)</span>}
        </label>
        {optional && value !== undefined && (
          <button
            type="button"
            onClick={() => { onChange(undefined); setInputVal(""); }}
            className="text-xs text-neko-gray hover:text-neko-orange transition-colors"
            aria-label="Wert zurücksetzen"
          >
            ✕ Kein Budget
          </button>
        )}
      </div>

      {/* Slider */}
      <div className="relative mt-2 mb-1">
        <input
          id={id}
          type="range"
          min={min}
          max={max}
          step={step}
          value={sliderValue}
          onChange={(e) => {
            const v = parseInt(e.target.value);
            onChange(v);
            setInputVal(String(v));
          }}
          aria-describedby={hint ? `${id}-hint` : undefined}
          className="w-full h-2 rounded-full appearance-none cursor-pointer accent-neko-orange bg-neko-dark-3"
          style={{ accentColor: "#f5a623" }}
        />
        {/* Bubble über Thumb — nur wenn Wert gesetzt */}
        {hasValue && (
          <div
            className="absolute -top-8 transform -translate-x-1/2 bg-neko-orange text-neko-dark text-xs font-semibold px-2 py-0.5 rounded pointer-events-none whitespace-nowrap"
            style={{ left: `calc(${thumbPct}% + ${8 - thumbPct * 0.16}px)` }}
            aria-hidden="true"
          >
            {formatEuro(sliderValue)}
          </div>
        )}
      </div>

      {/* Min / Max Labels */}
      <div className="flex justify-between text-xs text-neko-gray mt-1 mb-3">
        <span>{formatEuro(min)}</span>
        <span>{formatEuro(max)}</span>
      </div>

      {/* Manuelle Eingabe */}
      <div className="flex items-center gap-2">
        <input
          type="number"
          min={min}
          max={max}
          step={step}
          value={inputVal}
          placeholder="Oder Betrag eingeben"
          onChange={(e) => {
            setInputVal(e.target.value);
            const v = parseFloat(e.target.value);
            if (!isNaN(v) && v >= min && v <= max) onChange(Math.round(v / step) * step);
          }}
          onBlur={() => {
            // Snap to step on blur
            const v = parseFloat(inputVal);
            if (!isNaN(v)) {
              const snapped = Math.max(min, Math.min(max, Math.round(v / step) * step));
              onChange(snapped);
              setInputVal(String(snapped));
            }
          }}
          className={clsx(
            "flex-1 border rounded-lg px-3 py-2 text-neko-white text-sm bg-neko-dark-2/70",
            "border-neko-dark-3/60 focus:outline-none focus:ring-2 focus:ring-neko-orange/40 focus:border-neko-orange transition-all",
            "placeholder-neko-gray"
          )}
          aria-label={`${label} manuell eingeben`}
        />
        <span className="text-neko-gray text-sm">€</span>
      </div>

      {hint && (
        <p id={`${id}-hint`} className="text-xs text-neko-gray mt-1">{hint}</p>
      )}
    </div>
  );
}
